/* eslint-disable no-case-declarations */
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import EventTracker from 'src/pendo/EventTracker';
import PENDO_EVENT from 'src/pendo/events.constants';
import {
    readSwitchChatHistory,
    reportSwitchChatError,
    uploadSwitchChatHistory,
} from 'src/services/switch-chat-history.service';
import { TextChatMessage } from 'src/types/text-chat';
import { ChatKeyPair, generateChatKeyPair } from 'src/utils/chat-crypto.util';
import { ChatHistoryEntry } from 'src/utils/chat-history.util';
import useUser from 'src/hooks/useUser';
import { RootState } from 'src/store';
import { Room } from 'twilio-video';
import useParticipantType from 'src/hooks/useParticipantType';
import { useParticipants } from 'src/customer-call-sdk/hooks';
import publicIp from 'public-ip';
import Messaging, { OnMessageCallback } from '../../lib/messaging';
import * as CallAPI from '../../api';
import {
    CallState,
    MakeCall,
    JoinCall,
    RejectCall,
    SetActivePhoneNumber,
    Destroy,
    Initialize,
    HangupCall,
    AnswerCall,
    SetPendingSwitchDeviceName,
    SaveChatHistory,
    SendSwitchChatHistory,
    ClearSwitchChatHistory,
    CloseRating,
    CallRatingState,
    UserState,
    ShowVideoMailDialog,
    HideVideoMailDialog,
    SetDialogFlagTrue,
    SetDialogFlagFalse,
    ShowVideoMailAlertDialog,
    HideVideoMailAlerDialog,
    ShowAlertDialogOnCancel,
    HideAlertDialogOnCancel,
} from './types';
import CallContext from './CallContext';
import {
    UUID,
    IncomingMessageEventMap,
    TwilioVriCallPayload,
    VriRequestRatingPayload,
    VrsIncomingCallPayload,
    VrsOutgoingCallPayload,
    VrsRequestRatingPayload,
    VcoMutePayload,
    VcoUnMutePayload,
    P2pOutgoingCallPayload,
    P2pOutgoingCallCancelledPayload,
    VIBusyPayload,
    SwitchDeviceOfferPayload,
    SwitchDeviceChatHistoryPayload,
} from '../../types';
import LocalStorage from '../../lib/local-storage';
import getRequestById from '../../api/getRequestById';
import { WEBSOCKET_SUPPORTED } from '../../../config/common.constants';
import formatPhoneNumber from '../../lib/formatPhoneNumber';
import { useTwilioUserMediaContext } from '../TwilioUserMediaContext';
import { useToast } from '../../../context/ToastContext';
import Messages from '../../../config/messages.constants';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useSelfViewContext } from '../SelfViewContext';
import {
    muteVoiceConference,
    unmuteVoiceConference,
} from '../../../services/voice-conference.service';
// import * as CallAction from '../../../store/customer/actions';

import {
    SetCustomerNameAction,
    SET_CUSTOMER_NAME,
    CustomerState,
} from '../../../store/customer/types';

export const defaultUserState: UserState = {
    userId: null,
    deviceId: null,
    phoneNumber: null,
};
export const defaultCallState: CallState = {
    requestId: null,
    requestType: 'P2P',
    requestStatus: null,
    roomName: null,
    region: null,
    twilioRoomId: null,
    twilioVideoToken: null,
    twilioVoiceToken: null,
    toPhoneNumber: null,
    connected: false,
    isConnecting: false,
    isJoining: false,
    isIncomingCall: false,
    isOutgoingCall: false,
    interpreterName: null,
    interpreterNRPCDNumber: null,
    interpreterId: null,
    callerName: null,
    callerNumber: null,
    vco: false,
    ringTimeout: 15,
    providerName: null,
    callDuration: 0,
    calleeName: null,
    calleeNumber: null,
    position: null,
    isSwitchDeviceOffer: false,
    isDeviceSwitchJoin: false,
    switchSourceDeviceType: '',
    switchTargetDeviceType: '',
    switchOfferTitle: '',
    switchOfferBody: '',
    pendingSwitchDeviceName: '',
};

export const defaultCallRatingState: CallRatingState = {
    isRatingAvailable: false,
    ratingDetail: null,
};

export type OnIncomingMessageEventCallback = <T extends keyof IncomingMessageEventMap>(
    event: T,
    message: IncomingMessageEventMap[T],
) => void;

export const getCustomerName = (customerName: string): SetCustomerNameAction => {
    return {
        type: SET_CUSTOMER_NAME,
        payload: {
            customerName,
        },
    };
};

const messaging = new Messaging({ messagingType: WEBSOCKET_SUPPORTED ? 'websocket' : 'firebase' });

const CallProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
    const { customerNumbers, activeCustomerNumber } = useSelector<RootState, CustomerState>(
        (state) => state.customerDetail,
    );
    const toast = useToast();
    const pendingSwitchDeviceNameRef = useRef('');
    const chatHistoryRef = useRef<TextChatMessage[]>([]);
    // Target side of a switch: the private key the chat history is encrypted for, in memory only.
    const switchChatKeyRef = useRef<{ requestId: string; keyPair: ChatKeyPair } | null>(null);
    const [switchChatHistory, setSwitchChatHistory] = useState<ChatHistoryEntry[]>([]);
    const user = useUser();
    const { isMediaPermissionDenied, isMediaStopped, startMedia, audioDeviceId, videoDeviceId } =
        useTwilioUserMediaContext();
    const { turnedOff, turnOff, turnOn } = useSelfViewContext();
    const dispatch = useDispatch();
    const [initilaized, setInitialized] = useState(false);
    const [isNotificationEnabled, setNotificationEnabled] = useState(false);
    const [userState, setUserState] = useState<UserState>(defaultUserState);
    const [callState, setCallState] = useState<CallState>(defaultCallState);
    const [callRatingState, setCallRatingState] = useState<CallRatingState>(defaultCallRatingState);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [refreshedAccessToken, setRefreshedAccessToken] = useState<boolean | null>(null);
    const [isVideoMailDialogVisible, setVideoMailDialogVisible] = useState(false);
    const [videoMailDialogFlagVisible, setVideoMailDialogFlagVisible] = useState(false);
    const [requestIdForVedioMail, setRequestIdForVedioMail] = useState<string>('');
    const [messageForVideoMail, setMessageForVideoMail] = useState<string>('');
    const [isVideoMailAlertPopupVisible, setIsVideoMailAlertPopupVisible] = useState(false);
    const [isAlertPopupOnCancelVisible, setIsAlertPopupOnCancelVisible] = useState(false);
    const [isCameraTurnedOffBeforeCall, setIsCameraTurnedOffBeforeCall] = useState(false);

    const [twilioRoom, storeTwilioRoom] = useState<Room | null>(null);
    const trackingIntervalRef = useRef<NodeJS.Timeout | null>(null);

    const { participants } = useParticipants({ room: twilioRoom });
    const interpreterIdsRef = useRef<string[]>([]);

    const { participantTypeMap } = useParticipantType({
        participants,
        requestId: callState.requestId,
    });

    useEffect(() => {
        const ids = Array.from(participantTypeMap.entries())
            .filter(([, type]) => type.includes('interpreter'))
            .map(([id]) => id);
        interpreterIdsRef.current = ids;
    }, [participantTypeMap]);

    useEffect(() => {
        if (callState.connected && callState.requestType) {
            const callType = callState.requestType.replace('_OUTBOUND', '').replace('_INBOUND', '');
            const eventName = `activeConvoMinute${callType}`;
            trackingIntervalRef.current = setInterval(() => {
                const metadata = {
                    requestId: callState.requestId,
                    interpreterIds: interpreterIdsRef.current,
                };
                EventTracker.trackEvent(eventName, metadata);
            }, 60000);
        }

        return () => {
            if (trackingIntervalRef.current) {
                clearInterval(trackingIntervalRef.current);
                trackingIntervalRef.current = null;
            }
        };
    }, [callState.connected, callState.requestType]);

    const verifyMediaAvailable = useCallback(async () => {
        if (isMediaPermissionDenied) {
            toast.error(Messages.CAMERA_NOT_CONNECTED);
            return false;
        }

        if (isMediaStopped && audioDeviceId && videoDeviceId) {
            const isMediaStarted = await startMedia({
                type: 'exact',
                audioDeviceId,
                videoDeviceId,
            });

            if (!isMediaStarted) {
                toast.error(Messages.CAMERA_NOT_CONNECTED);
            }
            return isMediaStarted;
        }
        return true;
    }, [isMediaPermissionDenied, isMediaStopped, startMedia, audioDeviceId, videoDeviceId]);

    const turnOffSelfview = useCallback(() => {
        if (turnedOff) {
            turnOff();
        }
    }, [turnedOff, turnOff]);

    const clearError = () => {
        setErrorMessage(null);
    };

    // video mail method

    const showVideoMailDialog = useCallback<ShowVideoMailDialog>(() => {
        setVideoMailDialogVisible(true);
        setVideoMailDialogFlagVisible(false);
    }, []);

    const hideVideoMailDialog = useCallback<HideVideoMailDialog>(() => {
        setVideoMailDialogVisible(false);
        if (isCameraTurnedOffBeforeCall) {
            turnOff();
        } else {
            turnOn();
        }
    }, [isCameraTurnedOffBeforeCall, turnOn, turnOff]);

    const showVideoMailAlertDialog = useCallback<ShowVideoMailAlertDialog>(() => {
        setIsVideoMailAlertPopupVisible(true);
    }, []);

    const hideVideoMailAlerDialog = useCallback<HideVideoMailAlerDialog>(() => {
        setIsVideoMailAlertPopupVisible(false);
        // setVideoMailDialogFlagVisible(false);
        // setVideoMailDialogVisible(false);
        // setIsAlertPopupOnCancelVisible(false);
        if (isCameraTurnedOffBeforeCall) {
            turnOff();
        } else {
            turnOn();
        }
    }, [isCameraTurnedOffBeforeCall, turnOn, turnOff]);

    const setDialogFlagTrue = useCallback<SetDialogFlagTrue>(() => {
        setVideoMailDialogFlagVisible(true);
    }, []);

    const setDialogFlagFalse = useCallback<SetDialogFlagFalse>(() => {
        setVideoMailDialogFlagVisible(false);
    }, []);

    const handleMessageForVideoMail = (message: string) => {
        setMessageForVideoMail(message);
    };

    const handleRequestIdForVedioMail = (requestId: string) => {
        setRequestIdForVedioMail(requestId);
    };

    const showAlertDialogOnCancel = useCallback<ShowAlertDialogOnCancel>(() => {
        setIsAlertPopupOnCancelVisible(true);
    }, []);

    const hideAlertDialogOnCancel = useCallback<HideAlertDialogOnCancel>(() => {
        setIsAlertPopupOnCancelVisible(false);
    }, []);

    // video mail method ends

    const setTwilioVriPayload = useCallback((payload: TwilioVriCallPayload) => {
        setCallState((state) => ({
            ...state,
            interpreterName: payload.interpreterName,
            interpreterNRPCDNumber: payload.NRPCDNumber,
            providerName: payload.providerName,
            roomName: payload.mid,
            requestId: payload.requestID,
            interpreterId: payload.assignedInterpreter,
            requestStatus: payload.requestStatus,
            ringTimeout: payload.ringtimeout,
            isConnecting: false,
            isJoining: true,
            requestType: payload.requestType,
            twilioVideoToken: payload.twilioVideoToken,
        }));
    }, []);

    const setVriRequestRatingPayload = useCallback((payload: VriRequestRatingPayload) => {
        setCallRatingState((state) => ({
            ...state,
            isRatingAvailable: true,
            ratingDetail: {
                callDuration: payload.callDuration,
                providerName: payload.providerName,
                requestId: payload.requestID,
                interpreterName: payload.interpreterName,
                interpreterNRPCDNumber: payload.NRPCDNumber,
                interpreterId: payload.assignedInterpreter,
                requestStatus: payload.requestStatus,
            },
        }));
    }, []);

    const setVrsOutgoingPayload = useCallback((payload: VrsOutgoingCallPayload) => {
        setCallState((state) => ({
            ...state,
            calleeName: payload.calleeName,
            calleeNumber: payload.calleeNumber,
            roomName: payload.mid,
            requestId: payload.requestID,
            interpreterName: payload.interpreterName,
            interpreterNRPCDNumber: payload.NRPCDNumber,
            interpreterId: payload.assignedInterpreter,
            requestStatus: payload.requestStatus,
            ringTimeout: payload.ringtimeout,
            isOutgoingCall: true,
            isConnecting: false,
            isJoining: true,
            requestType: payload.requestType,
            twilioVideoToken: payload.twilioVideoToken,
            twilioVoiceToken: payload.twilioVoiceToken,
        }));
    }, []);

    const setVrsIncomingPayload = useCallback(
        (payload: VrsIncomingCallPayload) => {
            // toggleVideoMailFlags();
            setCallState((state) => ({
                ...state,
                callerName: payload.callerName,
                callerNumber: payload.callerNumber,
                interpreterName: payload.interpreterName,
                interpreterId: payload.assignedInterpreter,
                roomName: payload.mid,
                requestId: payload.requestID,
                ringTimeout: payload.ringtimeout,
                requestType: payload.requestType,
                isIncomingCall: true,
                isJoining: true,
            }));
        },
        [
            isVideoMailAlertPopupVisible,
            isVideoMailDialogVisible,
            isAlertPopupOnCancelVisible,
            videoMailDialogFlagVisible,
        ],
    );

    const setVrsRequestRatingPayload = useCallback((payload: VrsRequestRatingPayload) => {
        setCallRatingState((state) => ({
            ...state,
            isRatingAvailable: true,
            ratingDetail: {
                callDuration: payload.callDuration,
                providerName: payload.providerName,
                requestId: payload.requestID,
                interpreterName: payload.interpreterName,
                interpreterNRPCDNumber: payload.NRPCDNumber,
                interpreterId: payload.assignedInterpreter,
                requestStatus: payload.requestStatus,
            },
        }));
    }, []);

    const setToggleDeafAudiofromInterpreter = useCallback((vco: boolean, roomName: UUID) => {
        if (vco) {
            unmuteVoiceConference({ roomname: roomName });
        } else {
            muteVoiceConference({ roomname: roomName });
        }
    }, []);

    const setVCO = useCallback((vco: boolean, roomName: UUID) => {
        setCallState((state) => ({ ...state, vco, roomName }));
    }, []);

    const attachRefreshedToken = () => {
        setRefreshedAccessToken(true);
    };

    const setVrsIncomingCallAnsweredPayload = useCallback(() => {
        setCallState((state) => ({ ...state, ...defaultCallState }));
    }, []);

    const setP2pOutgoingCallPayload = useCallback((payload: P2pOutgoingCallPayload) => {
        setCallState((state) => ({
            ...state,
            calleeName: payload.calleeName,
            calleeNumber: payload.calleeNumber,
            roomName: payload.mid,
            requestId: payload.requestID,
            requestStatus: payload.requestStatus,
            ringTimeout: payload.ringtimeout,
            isOutgoingCall: true,
            isConnecting: false,
            isJoining: true,
            isVideoMailDialogVisible: false,
        }));
    }, []);

    const setP2pOutgoingCallCancelledPayload = useCallback(
        (payload: P2pOutgoingCallCancelledPayload) => {
            turnOff();
            if (payload.requestStatus === 'cancelled') {
                handleMessageForVideoMail(payload.body);
                handleRequestIdForVedioMail(payload.requestID);
                showAlertDialogOnCancel();
            }
            setCallState((state) => ({ ...state, ...defaultCallState }));
        },
        [turnOff],
    );

    const setVIBusy = useCallback((payload: VIBusyPayload) => {
        setCallState((state) => ({ ...state, ...defaultCallState }));
        setErrorMessage(payload.body);
    }, []);

    const setSwitchDeviceOfferPayload = useCallback((payload: SwitchDeviceOfferPayload) => {
        EventTracker.trackEvent(PENDO_EVENT.SWITCH_OFFER_RECEIVED, {
            requestId: payload.requestID,
            targetDeviceType: payload.toDeviceType,
        });

        setCallState((state) => ({
            ...state,
            requestId: payload.requestID,
            roomName: payload.mid,
            requestType: payload.requestType,
            isSwitchDeviceOffer: true,
            switchSourceDeviceType: payload.fromDeviceType,
            switchTargetDeviceType: payload.toDeviceType,
            switchOfferTitle: payload.notificationTitle ?? '',
            switchOfferBody: payload.notificationBody ?? '',
        }));
    }, []);

    const setPendingSwitchDeviceName = useCallback<SetPendingSwitchDeviceName>((deviceName) => {
        pendingSwitchDeviceNameRef.current = deviceName;
        setCallState((state) => ({ ...state, pendingSwitchDeviceName: deviceName }));
    }, []);

    const saveChatHistory = useCallback<SaveChatHistory>((messages) => {
        chatHistoryRef.current = messages;
    }, []);

    /** Source side, on eviction: the chat as it was the moment the other device took over. */
    const sendSwitchChatHistory = useCallback<SendSwitchChatHistory>((requestId) => {
        const messages = chatHistoryRef.current;
        chatHistoryRef.current = [];

        uploadSwitchChatHistory(requestId, messages);
    }, []);

    const clearSwitchChatState = useCallback(() => {
        chatHistoryRef.current = [];
        switchChatKeyRef.current = null;
        setSwitchChatHistory([]);
    }, []);

    const setSwitchDeviceCompletedPayload = useCallback(() => {
        const deviceName = pendingSwitchDeviceNameRef.current;
        pendingSwitchDeviceNameRef.current = '';

        if (deviceName) {
            toast.success(Messages.SWITCHED_TO_DEVICE_MESSAGE(deviceName), {
                banner: 1,
                icon: <CheckCircleIcon sx={{ fontSize: 20 }} />,
                autoHideMs: 4000,
            });
        }
        setCallState((state) => ({ ...state, pendingSwitchDeviceName: '' }));
    }, [toast]);

    const setSwitchDeviceCancelledPayload = useCallback(() => {
        setCallState((state) => ({ ...state, isSwitchDeviceOffer: false }));
    }, []);

    const setSwitchDeviceChatHistoryPayload = useCallback(
        async (payload: SwitchDeviceChatHistoryPayload) => {
            const key = switchChatKeyRef.current;
            switchChatKeyRef.current = null;

            const entries = await readSwitchChatHistory(
                payload.messages,
                key?.requestId === payload.requestID ? key.keyPair : undefined,
                payload.requestID,
            );
            if (entries.length) {
                setSwitchChatHistory(entries);
            }
        },
        [],
    );

    const clearSwitchChatHistory = useCallback<ClearSwitchChatHistory>(
        () => setSwitchChatHistory([]),
        [],
    );

    const onIncomingMessageEvent: OnIncomingMessageEventCallback = (event, message) => {
        switch (event) {
            case 'TWILIO_VRI_CALL':
                setTwilioVriPayload(message as TwilioVriCallPayload);
                break;
            case 'VRI_REQUEST_RATING':
                setVriRequestRatingPayload(message as VriRequestRatingPayload);
                break;
            case 'VRS_INCOMING_CALL':
                setVrsIncomingPayload(message as VrsIncomingCallPayload);
                break;
            case 'VRS_OUTGOING_CALL':
                setVrsOutgoingPayload(message as VrsOutgoingCallPayload);
                break;
            case 'VRS_REQUEST_RATING':
                setVrsRequestRatingPayload(message as VrsRequestRatingPayload);
                break;
            case 'VCO_MUTE':
                setVCO(false, (message as VcoMutePayload).mid);
                setToggleDeafAudiofromInterpreter(false, (message as VcoMutePayload).mid);
                break;
            case 'VCO_UNMUTE':
                setVCO(true, (message as VcoUnMutePayload).mid);
                setToggleDeafAudiofromInterpreter(true, (message as VcoUnMutePayload).mid);
                break;
            case 'DEVICE_REGISTER':
                attachRefreshedToken();
                break;
            case 'VRS_INCOMING_CALL_ANSWERED':
                setVrsIncomingCallAnsweredPayload();
                break;
            case 'P2P_OUTGOING_CALL':
                setP2pOutgoingCallPayload(message as P2pOutgoingCallPayload);
                break;
            case 'P2P_OUTGOING_CALL_CANCELLED':
                setP2pOutgoingCallCancelledPayload(message as P2pOutgoingCallCancelledPayload);
                break;
            case 'VI_BUSY':
                setVIBusy(message as VIBusyPayload);
                break;
            case 'SWITCH_DEVICE_OFFER':
                setSwitchDeviceOfferPayload(message as SwitchDeviceOfferPayload);
                break;
            case 'SWITCH_DEVICE_COMPLETED':
                setSwitchDeviceCompletedPayload();
                break;
            case 'SWITCH_DEVICE_CANCELLED':
                setSwitchDeviceCancelledPayload();
                break;
            case 'SWITCH_DEVICE_CHAT_HISTORY':
                setSwitchDeviceChatHistoryPayload(message as SwitchDeviceChatHistoryPayload);
                break;
            default:
                break;
        }
    };

    const handleMessage: OnMessageCallback = (event, message) => {
        switch (event) {
            case 'notification_enabled':
                setNotificationEnabled(message);
                break;
            case 'message':
                onIncomingMessageEvent(message.push_type, message);
                break;
            default:
                break;
        }
    };

    const init = useCallback<Initialize>(
        ({ userId, authToken, deviceId, onAuthFailure }) => {
            if (!initilaized) {
                setUserState((state) => ({ ...state, userId, deviceId }));
                messaging.init({ userId, authToken, deviceId });
                messaging.onMessage(handleMessage);
                messaging.onError(setErrorMessage);
                if (onAuthFailure) {
                    messaging.onAuthFailure(onAuthFailure);
                }
                setInitialized(true);
            }
        },
        [initilaized],
    );

    const destroy = useCallback<Destroy>(() => {
        if (messaging) {
            messaging.destroy();
        }
        setInitialized(false);
        setRefreshedAccessToken(null);
        setUserState(defaultUserState);
        setCallState(defaultCallState);
        setCallRatingState(defaultCallRatingState);
        dispatch(getCustomerName(''));
    }, [messaging]);

    const setActivePhoneNumber = useCallback<SetActivePhoneNumber>((phoneNumber) => {
        setUserState((state) => ({ ...state, phoneNumber }));
    }, []);

    const makeCall = useCallback<MakeCall>(
        async ({ toPhoneNumber, specialityTag = '' }) => {
            const isMediaAvailable = await verifyMediaAvailable();
            if (!isMediaAvailable) {
                return;
            }
            const cameraBeforeCall = LocalStorage.get('TURN_OFF_SELFVIEW', 'false') === 'true';
            setIsCameraTurnedOffBeforeCall(Boolean(cameraBeforeCall));

            const { phoneNumber, deviceId } = userState;
            if (phoneNumber && deviceId) {
                setCallState((state) => ({ ...state, toPhoneNumber, isConnecting: true }));
                const toPhoneNumberWithPrefix = toPhoneNumber
                    ? formatPhoneNumber(toPhoneNumber)
                    : null;

                const ipAddress = await publicIp.v4();
                const { data, error } = await CallAPI.makeCall(
                    {
                        fromNumber: phoneNumber,
                        toNumber: toPhoneNumberWithPrefix,
                        specialityTags: specialityTag,
                        deviceId,
                    },
                    {
                        headers: {
                            'X-Client-IP': ipAddress,
                        },
                    },
                );

                if (data) {
                    const { callDetail } = data;
                    const isP2P = callDetail.requestType === 'P2P';
                    const isCustomerCare = callDetail.requestType === 'P2P_OUTBOUND';

                    setCallState((state) => ({
                        ...state,
                        region: callDetail.region,
                        requestId: callDetail.requestId,
                        requestType: callDetail.requestType,
                        roomName: isP2P || isCustomerCare ? callDetail.roomname : null,
                        twilioRoomId: isP2P || isCustomerCare ? callDetail.roomid : null,
                        twilioVideoToken: isP2P || isCustomerCare ? callDetail.token : null,
                        isConnecting: !isP2P,
                        isOutgoingCall: true,
                        isJoining: false,
                        connected: isP2P,
                        position: callDetail.position,
                    }));
                    if (user && activeCustomerNumber) {
                        EventTracker.setAccountData(
                            user,
                            activeCustomerNumber,
                            customerNumbers.length,
                            callDetail.requestId,
                            callDetail.requestType,
                        );
                    }
                    EventTracker.trackEvent('callRequest', { requestId: callDetail.requestId });
                    EventTracker.trackEvent('callPositionInQueue', {
                        position: callDetail.position,
                    });
                }

                if (error) {
                    if (data && data?.callDetail.requestType === 'P2P_OUTBOUND') {
                        handleMessageForVideoMail(
                            'Customer Support is not available to take your call, please try again later.',
                        );
                        handleRequestIdForVedioMail(data.callDetail.requestId);
                        turnOff();
                        showVideoMailAlertDialog();
                    } else {
                        setErrorMessage(error);
                    }
                    setCallState(defaultCallState);
                }
            }
        },
        [userState, verifyMediaAvailable],
    );

    const answer = useCallback<AnswerCall>(async () => {
        const isMediaAvailable = await verifyMediaAvailable();
        if (!isMediaAvailable) {
            return;
        }
        if (callState.requestId && userState.deviceId) {
            const ipAddress = await publicIp.v4();
            const chatKeyPair = callState.isSwitchDeviceOffer
                ? await generateChatKeyPair().catch((error) => {
                      reportSwitchChatError('key', callState.requestId ?? '', error);
                      return undefined;
                  })
                : undefined;
            switchChatKeyRef.current = chatKeyPair
                ? { requestId: callState.requestId, keyPair: chatKeyPair }
                : null;
            const { data, error } = await CallAPI.answerCall(
                {
                    requestId: callState.requestId,
                    deviceId: userState.deviceId,
                    isTransfer: callState.isSwitchDeviceOffer,
                    chatPublicKey: chatKeyPair?.publicKey,
                },
                {
                    headers: {
                        'X-Client-IP': ipAddress,
                    },
                },
            );
            if (data) {
                const { callDetail } = data;
                setCallState((state) => ({
                    ...state,
                    region: callDetail.region,
                    // roomName: callDetail.roomname,
                    // twilioRoomId: callDetail.roomid,
                    twilioVideoToken: callDetail.token,
                    twilioVoiceToken: callDetail.twilioVoiceToken,
                    isConnecting: false,
                    isJoining: false,
                    connected: true,
                    position: 0,
                    isSwitchDeviceOffer: false,
                    isDeviceSwitchJoin: callState.isSwitchDeviceOffer,
                }));
                if (user && activeCustomerNumber) {
                    EventTracker.setAccountData(
                        user,
                        activeCustomerNumber,
                        customerNumbers.length,
                        callState.requestId,
                        callState.requestType,
                    );
                }
            }
            if (error) {
                if (callState.isSwitchDeviceOffer) {
                    EventTracker.trackEvent(PENDO_EVENT.SWITCH_FAILED, {
                        requestId: callState.requestId,
                        reason: error,
                    });
                }
                switchChatKeyRef.current = null;
                setCallState((state) => ({ ...state, ...defaultCallState }));
                setErrorMessage(error);
            }
            turnOffSelfview();
        }
    }, [callState, userState, verifyMediaAvailable]);

    const closeAnswer = useCallback(() => {
        setCallState((state) => ({ ...state, ...defaultCallState }));
    }, []);

    const join = useCallback<JoinCall>(async () => {
        if (callState.requestId) {
            const { data, error } = await getRequestById({ id: callState.requestId });
            if (data) {
                // TODO: Validate Request Details
                const { callRequest } = data;
                if (callState.roomName === callRequest.meetingId) {
                    setCallState((state) => ({
                        ...state,
                        interpreterId: callRequest.assignedInterpreter,
                        isConnecting: false,
                        isJoining: false,
                        connected: true,
                    }));
                    if (user && activeCustomerNumber) {
                        EventTracker.setAccountData(
                            user,
                            activeCustomerNumber,
                            customerNumbers.length,
                            callRequest.requestId,
                            callRequest.requestType,
                        );
                    }
                } else {
                    setCallState((state) => ({ ...state, ...defaultCallState }));
                    setErrorMessage('Invalid Call');
                }
            }
            if (error) {
                setErrorMessage(error);
            }
        }
    }, [callState]);

    const reject = useCallback<RejectCall>(async () => {
        if (callState.requestId && userState.userId) {
            const { data, error } = await CallAPI.rejectCall({
                requestId: callState.requestId,
                participantId: userState.userId,
            });
            handleRequestIdForVedioMail(callState.requestId);
            if (data) {
                setCallState((state) => ({ ...state, ...defaultCallState }));
            }

            if (error) {
                setErrorMessage(error);
            }
            turnOffSelfview();
            dispatch(getCustomerName(''));
        }
    }, [callState, userState, turnOffSelfview]);

    const hangup = useCallback<HangupCall>(async () => {
        if (callState.roomName) {
            const { data, error } = await CallAPI.hangupCall({
                roomname: callState.roomName,
            });

            if (data) {
                clearSwitchChatState();
                setCallState((state) => ({
                    ...state,
                    ...defaultCallState,
                }));
            }

            if (error) {
                setErrorMessage(error);
            }
            turnOffSelfview();
            dispatch(getCustomerName(''));
        }
    }, [callState, turnOffSelfview]);

    const disconnect = useCallback(() => {
        pendingSwitchDeviceNameRef.current = '';
        clearSwitchChatState();
        setCallState(defaultCallState);
        turnOffSelfview();
        dispatch(getCustomerName(''));
    }, [callState, turnOffSelfview]);

    const closeRating = useCallback<CloseRating>(() => {
        setCallRatingState((state) => ({ ...state, ...defaultCallRatingState }));
    }, []);

    const getVideoInterpreter = useCallback(() => {
        makeCall({ specialityTag: 'vi', toPhoneNumber: null });
    }, [makeCall]);

    const setVco = useCallback((vco: boolean) => {
        setCallState((state) => ({ ...state, vco }));
    }, []);

    return (
        <CallContext.Provider
            value={{
                init,
                destroy,
                isNotificationEnabled,
                setActivePhoneNumber,
                makeCall,
                answer,
                setPendingSwitchDeviceName,
                saveChatHistory,
                sendSwitchChatHistory,
                switchChatHistory,
                clearSwitchChatHistory,
                closeAnswer,
                join,
                reject,
                hangup,
                disconnect,
                closeRating,
                getVideoInterpreter,
                error: errorMessage,
                clearError,
                refreshedAccessToken,
                setVco,
                ...userState,
                ...callState,
                ...callRatingState,
                showVideoMailDialog, // video mail
                hideVideoMailDialog, // video mail
                isVideoMailDialogVisible, // video mail
                requestIdForVedioMail, // video mail
                messageForVideoMail, // video mail
                setDialogFlagTrue, // video mail
                setDialogFlagFalse, // video mail
                videoMailDialogFlagVisible, // video mail
                handleMessageForVideoMail, // video mail
                handleRequestIdForVedioMail, // video mail
                showVideoMailAlertDialog, // video mail
                hideVideoMailAlerDialog, // video mail
                isVideoMailAlertPopupVisible, // video mail
                showAlertDialogOnCancel, // video mail
                hideAlertDialogOnCancel, // video mail
                isAlertPopupOnCancelVisible, // video mail
                isCameraTurnedOffBeforeCall,
                twilioRoom,
                storeTwilioRoom,
            }}
        >
            {children}
        </CallContext.Provider>
    );
};

export default CallProvider;
